import { Link } from 'react-router-dom'
import type { OpusAnalysisResponse } from '../../types/research'

function DivergenceBadge({ value }: { value: number | null }) {
  if (value == null) return <span className="text-slate-500">—</span>
  const rich = value > 0
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        rich
          ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
          : 'bg-sky-500/10 text-sky-600 dark:text-sky-400'
      }`}
    >
      {rich ? '+' : ''}
      {value} pts {rich ? 'rich' : 'cheap'}
    </span>
  )
}

/** Renders the summary's paragraphs and "- " bullet groups. */
function SummaryText({ text }: { text: string }) {
  const blocks = text.split(/\n\n+/)
  return (
    <div className="space-y-3">
      {blocks.map((block, i) => {
        const lines = block.split('\n')
        if (lines.every((l) => l.trim().startsWith('- '))) {
          return (
            <ul key={i} className="list-disc pl-5 space-y-1 text-sm text-slate-600 dark:text-slate-300">
              {lines.map((l, j) => (
                <li key={j}>{l.trim().slice(2)}</li>
              ))}
            </ul>
          )
        }
        return (
          <p key={i} className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-line">
            {block}
          </p>
        )
      })}
    </div>
  )
}

export default function AnalysisResultView({ result }: { result: OpusAnalysisResponse }) {
  return (
    <div className="space-y-5">
      {/* Model + demo notice */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
          🧠 {result.model}
        </span>
        <span className="text-xs text-slate-500">
          {new Date(result.createdAt).toLocaleString()}
        </span>
      </div>

      <SummaryText text={result.summary} />

      {result.keyFindings.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">Key findings</h4>
          <ul className="space-y-1.5">
            {result.keyFindings.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {f}
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.flagged.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
            Flagged for review ({result.flagged.length})
          </h4>
          <div className="space-y-2">
            {result.flagged.map((f) => (
              <div
                key={f.symbol}
                className="bg-slate-100/70 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700/50 rounded-lg p-3"
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/dashboard/financial-app/fundamentals/${f.symbol}`}
                      className="font-semibold text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {f.symbol}
                    </Link>
                    <span className="text-sm text-slate-600 dark:text-slate-300">{f.name}</span>
                    <span className="text-xs text-slate-500">{f.sector}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <DivergenceBadge value={f.volDivergence} />
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                      score {f.score}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1.5">{f.reason}</p>
                <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
                  {f.nextEarningsDate && (
                    <span className="text-slate-500">Earnings {f.nextEarningsDate}</span>
                  )}
                  {f.relatedFilings.map((fil) => (
                    <a
                      key={fil.accessionNumber}
                      href={fil.edgarUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {fil.formType} ({fil.filedAt}) ↗
                    </a>
                  ))}
                  <Link
                    to={`/dashboard/financial-app/options/${f.symbol}`}
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Options chain
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {result.offerings.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
            Security offerings ({result.offerings.length})
          </h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {result.offerings.map((o) => (
              <div
                key={`${o.symbol}-${o.instrument}`}
                className="bg-slate-100/70 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700/50 rounded-lg p-4"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-semibold text-sm text-slate-900 dark:text-white">
                    {o.symbol} · {o.instrument}
                  </span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed mb-3">{o.summary}</p>
                <dl className="space-y-1">
                  {o.keyTerms.map((t) => (
                    <div key={t.label} className="flex justify-between gap-3 text-xs">
                      <dt className="text-slate-500">{t.label}</dt>
                      <dd className="text-slate-700 dark:text-slate-200 text-right">{t.value}</dd>
                    </div>
                  ))}
                </dl>
                {o.sourceFiling && (
                  <a
                    href={o.sourceFiling.edgarUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block mt-3 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Source: {o.sourceFiling.formType} filed {o.sourceFiling.filedAt} ↗
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {result.citations.length > 0 && (
        <div className="flex flex-wrap gap-3 pt-1 border-t border-slate-200 dark:border-slate-700/50">
          {result.citations.map((c) => (
            <a
              key={c.url}
              href={c.url}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-2"
            >
              {c.label} ↗
            </a>
          ))}
        </div>
      )}
    </div>
  )
}
